/*
  Keeps add-on cart lines in step with the line they belong to.

  Add-ons are added as their own cart lines (see snippets/product-addons.liquid
  and the items[] branch in product-form.js), which keeps inventory and
  fulfilment honest but leaves the two lines independent once they are in the
  cart. Without this, changing 2 racks to 3 leaves 2 fitments behind.

  Each add-on line carries a `_addon_for` property holding its parent's variant
  id. After any cart update this reconciles the whole cart in one pass: every
  add-on line is set to its parent's quantity, or removed outright when the
  parent is gone.

  Reconciling after the fact, rather than intercepting each quantity control,
  means this works the same on the cart page, in the drawer, and for any other
  path that changes the cart - including ones added later.

  Requires: pubsub.js, constants.js (both loaded globally in theme.liquid).
*/

(function () {
  const PARENT_PROPERTY = '_addon_for';
  const SOURCE = 'cart-addon-sync';

  // Guards against reacting to our own update and looping. The reconcile pass
  // publishes cartUpdate so the cart UI redraws, which would otherwise bring us
  // straight back in here.
  //
  // Set synchronously at the top of reconcile(), before the first await. It used
  // to be set after the cart fetch, which meant two cartUpdate events in the
  // same tick both cleared the check before either set the flag, and each ran a
  // full pass. The guard collapsed nothing - it only looked like it did.
  let reconciling = false;

  // A cart change that arrives mid-pass is replayed once the pass finishes,
  // rather than dropped, so the customer's last action is always reconciled.
  let pending = false;

  let debounceTimer = null;

  // A single customer action publishes cartUpdate more than once - the change
  // itself, then the section redraws that follow it. Collapsing those into one
  // pass is the difference between several cart requests per click and one.
  //
  // That matters because Shopify rate-limits the cart endpoints and starts
  // returning 429 once a burst goes over, which is what happened while testing
  // add-to-cart on 2026-08-11. 50ms is well under the network round trip this
  // replaces, so nothing feels slower.
  const DEBOUNCE_MS = 50;

  function parentQuantities(items) {
    const quantities = {};
    items.forEach((item) => {
      if (item.properties && item.properties[PARENT_PROPERTY]) return;
      const id = String(item.variant_id);
      quantities[id] = (quantities[id] || 0) + item.quantity;
    });
    return quantities;
  }

  function buildUpdates(items) {
    const quantities = parentQuantities(items);
    const updates = {};

    items.forEach((item) => {
      const parentId = item.properties && item.properties[PARENT_PROPERTY];
      if (!parentId) return;

      // No parent left in the cart means the rack was removed, so the add-on
      // goes with it - a lone fitment line is never what anyone wanted.
      const target = quantities[String(parentId)] || 0;
      if (item.quantity !== target) updates[item.key] = target;
    });

    return updates;
  }

  // Sections Dawn does not redraw in response to a cartUpdate event. Its own
  // quantity handler refreshes these through the sections parameter, but the
  // onCartUpdate path that a published event triggers only redraws the cart
  // items - which left the bag icon showing the pre-sync count.
  function sectionsToRender() {
    const candidates = [
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      { id: 'main-cart-footer', section: null, selector: '.js-contents' },
    ];

    return candidates.reduce((sections, candidate) => {
      const element = document.getElementById(candidate.id);
      if (!element) return sections;

      // Section ids are dynamic for template sections and fixed for the rest.
      const section = candidate.section || element.dataset.id;
      if (section) sections.push({ ...candidate, section });
      return sections;
    }, []);
  }

  function sectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  function applySections(sections, rendered) {
    if (!rendered) return;

    sections.forEach((section) => {
      const host = document.getElementById(section.id);
      if (!host) return;

      const target = host.querySelector(section.selector) || host;
      const html = rendered[section.section];
      if (!html) return;

      const inner = sectionInnerHTML(html, section.selector);
      if (inner !== null) target.innerHTML = inner;
    });
  }

  // The published event often carries the whole cart already, in which case
  // fetching it again is a wasted request on every cart change site-wide -
  // including for the majority of products that have no add-ons at all and
  // never will.
  //
  // Only a genuinely complete cart qualifies. A /cart/update.js response is one.
  // An items[] add is NOT: it returns only the lines it just added, with no
  // item_count, and treating that as the whole cart would read every other line
  // as gone and strip add-ons that should have stayed. Hence the item_count
  // test rather than a bare items check.
  function fullCartOrNull(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return null;
    if (typeof cartData.item_count !== 'number') return null;
    return cartData;
  }

  function schedule(cartData) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      reconcile(cartData);
    }, DEBOUNCE_MS);
  }

  async function reconcile(cartData) {
    if (reconciling) {
      pending = true;
      return;
    }
    reconciling = true;

    try {
      const cart =
        fullCartOrNull(cartData) ||
        (await fetch(`${routes.cart_url}.js`).then((response) => response.json()));
      if (!cart || !cart.items || cart.items.length === 0) return;

      const updates = buildUpdates(cart.items);
      if (Object.keys(updates).length === 0) return;

      reconciling = true;

      const sections = sectionsToRender();
      const response = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          updates,
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      });
      const parsedState = await response.json();

      applySections(sections, parsedState.sections);

      // Re-published so the cart page, drawer and icon bubble redraw against
      // the corrected cart. CartItems ignores events it raised itself, so this
      // has to come from a different source name to be picked up.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: parsedState });
    } catch (error) {
      // A failed reconcile leaves the cart as the customer last saw it, which
      // is wrong but coherent - better than tearing the page down over it.
      console.error('Add-on sync failed', error);
    } finally {
      reconciling = false;

      // Replayed with no cart data, so the pass refetches rather than trusting
      // whatever happened to be current when the dropped event fired.
      if (pending) {
        pending = false;
        schedule(null);
      }
    }
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === SOURCE) return;
    schedule(event.cartData);
  });
})();
